/**
 * Daily Self-Assessment — Rotation engine + data model.
 *
 * Each operating day, staff assesses ONE facility area across all relevant
 * domains. Each item gets: photo + green/yellow/red rating + optional note.
 *
 * Rating scale:
 *   1 = Green (Compliant) — meets standard, no action needed
 *   2 = Yellow (Concern)  — minor issue, needs attention
 *   3 = Red (Finding)     — deficiency, requires corrective action
 */

import type { FacilityArea, SurveyDomain } from './survey'
import { getOperatingDayNumber, isNonOperatingDay } from './dailyAssessmentSchedule'

// ── Types ───────────────────────────────────────────────────────────────────

export type AssessmentRating = 1 | 2 | 3

export const RATING_LABELS: Record<AssessmentRating, string> = {
  1: 'Compliant',
  2: 'Concern',
  3: 'Finding',
}

export const RATING_COLORS: Record<AssessmentRating, { bg: string; text: string; border: string }> = {
  1: { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-300' },
  2: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-300' },
  3: { bg: 'bg-rose-100', text: 'text-rose-700', border: 'border-rose-300' },
}

export interface DailyAssessmentItem {
  item_id: string
  label: string
  domain: SurveyDomain
  standard_ref: string
  evidence_type: string[]
  control_type?: 'rotational' | 'operational'
  config_key?: DailyOperationalControlKey
  requires_severity?: boolean
  /**
   * When true, this item is a verification check (log/inventory/physical)
   * where a Yes answer still requires a photo of the verifying evidence
   * (e.g., a photo of the glucometer QC test result log). Non-verification
   * items only require evidence on No answers.
   */
  verification?: boolean
  /**
   * Operator-facing instruction shown with the Yes evidence prompt for
   * verification items. Example: "Capture image of the glucometer quality
   * check test results."
   */
  yes_evidence_prompt?: string
}

export interface DailyAssessmentPlan {
  rotationDay: number
  area: FacilityArea
  combinedAreas: FacilityArea[] // when small areas are grouped (e.g., Clean + Soiled Utility)
  domains: SurveyDomain[]
  items: DailyAssessmentItem[]
  estimatedMinutes: number
}

export interface DailyAssessmentResponse {
  item_id: string
  response: DailyAssessmentAnswer
  rating: AssessmentRating
  severity: AssessmentRating | null
  photo_id: string | null
  note: string
  assessed_at: string
}

export interface DailyAssessmentResult {
  date: string
  area: FacilityArea
  responses: DailyAssessmentResponse[]
  average_rating: number
  completed_at: string | null
}

export type DailyAssessmentAnswer = 'yes' | 'no' | 'na'

export interface DailyAssessmentFacilityConfig {
  operationalControls?: Partial<Record<DailyOperationalControlKey, boolean>>
}

// ── Skip Day ───────────────────────────────────────────────────────────────

export const SKIP_REASONS = [
  'Facility closed (unplanned)',
  'Staffing shortage',
  'Emergency / incident response',
  'Equipment maintenance in area',
  'Accreditation survey in progress',
  'Other',
] as const

export type SkipReason = (typeof SKIP_REASONS)[number]

export interface DailySkipRecord {
  date: string
  area: FacilityArea
  reason: SkipReason
  note: string
  skipped_by: string
  skipped_at: string
}

// ── Rotation Schedule ───────────────────────────────────────────────────────

interface RotationSlot {
  areas: FacilityArea[]
  domains: SurveyDomain[]
  label: string
}

const DEFAULT_ROTATION: RotationSlot[] = [
  {
    areas: ['Lobby'],
    domains: ['IC', 'ENV', 'LSC', 'COMP', 'ADA'],
    label: 'Lobby',
  },
  {
    areas: ['Pre-Op'],
    domains: ['IC', 'MED', 'ENV', 'LSC'],
    label: 'Pre-Op',
  },
  {
    areas: ['OR 1'],
    domains: ['IC', 'SPD', 'ENV', 'LSC', 'MED'],
    label: 'OR 1',
  },
  {
    areas: ['OR 2'],
    domains: ['IC', 'SPD', 'ENV', 'LSC', 'MED'],
    label: 'OR 2',
  },
  {
    areas: ['PACU'],
    domains: ['IC', 'MED', 'ENV', 'LSC'],
    label: 'PACU',
  },
  {
    areas: ['SPD'],
    domains: ['IC', 'SPD', 'ENV'],
    label: 'Sterile Processing',
  },
  {
    areas: ['Med Storage'],
    domains: ['MED', 'ENV', 'LSC'],
    label: 'Medication Storage',
  },
  {
    areas: ['Admin Office'],
    domains: ['HR', 'COMP', 'CR'],
    label: 'Admin Office',
  },
  {
    areas: ['Mechanical'],
    domains: ['LSC', 'ENV', 'EM'],
    label: 'Mechanical / Electrical',
  },
  {
    areas: ['Clean Utility', 'Soiled Utility'],
    domains: ['IC', 'ENV', 'WASTE'],
    label: 'Clean & Soiled Utility',
  },
  {
    areas: ['Corridor', 'Restroom'],
    domains: ['ENV', 'LSC', 'ADA'],
    label: 'Corridor & Restroom',
  },
  {
    areas: ['Staff Lounge', 'Exterior'],
    domains: ['ENV', 'ADA', 'WASTE'],
    label: 'Staff Lounge & Exterior',
  },
  {
    areas: ['OR 3', 'OR 4'],
    domains: ['IC', 'SPD', 'ENV', 'LSC', 'MED'],
    label: 'OR 3 / OR 4 (or high-risk repeat)',
  },
]

// ── Quick assessment items per domain ───────────────────────────────────────
// These are the daily check items — short, photo-friendly, ratable green/yellow/red

const DOMAIN_ITEMS: Record<string, Array<{ label: string; standard_ref: string }>> = {
  IC: [
    { label: 'Hand hygiene station — soap, sanitizer, signage', standard_ref: '§416.51(a) IC' },
    { label: 'PPE availability — gloves, masks, gowns, eye protection', standard_ref: '§416.51(a) IC' },
    { label: 'Sharps containers — not overfull, properly mounted', standard_ref: '§416.51(a) IC' },
    { label: 'Surface cleanliness — counters, equipment, high-touch areas', standard_ref: '§416.51(b) IC' },
  ],
  SPD: [
    { label: 'Sterilizer log — current, documented, parameters met', standard_ref: '§416.51(b) SPD' },
    { label: 'Instrument packaging — intact, indicators visible', standard_ref: '§416.51(b) SPD' },
    { label: 'Clean/dirty separation — no cross-contamination path', standard_ref: '§416.51(b) SPD' },
  ],
  ENV: [
    { label: 'General cleanliness — floors, walls, ceiling tiles', standard_ref: '§416.44(a) ENV' },
    { label: 'Lighting — adequate, all fixtures working', standard_ref: '§416.44(a) ENV' },
    { label: 'Temperature/humidity — within posted range', standard_ref: '§416.44(a) ENV' },
    { label: 'Equipment condition — no damage, cords intact', standard_ref: '§416.44(b) ENV' },
  ],
  LSC: [
    { label: 'Fire extinguisher — visible, accessible, current tag', standard_ref: '§416.44(b) LSC' },
    { label: 'Exit signs — illuminated, unobstructed path', standard_ref: '§416.44(b) LSC' },
    { label: 'Electrical panels — 36" clearance, labeled', standard_ref: '§416.44(b) LSC' },
    { label: 'Sprinkler heads — unobstructed, 18" clearance below', standard_ref: '§416.44(b) LSC' },
  ],
  MED: [
    { label: 'Medication storage — locked, organized, labeled', standard_ref: '§416.48 MED' },
    { label: 'Refrigerator temp log — current, within range', standard_ref: '§416.48 MED' },
    { label: 'Expiration dates — no expired medications visible', standard_ref: '§416.48 MED' },
  ],
  COMP: [
    { label: 'Patient rights posting — visible, current version', standard_ref: '§416.50(a) COMP' },
    { label: 'Grievance process — posted, accessible to patients', standard_ref: '§416.50(a) COMP' },
  ],
  HR: [
    { label: 'Staff credentials — current licenses posted or on file', standard_ref: '§416.45 HR' },
    { label: 'Training records — current competency documentation', standard_ref: '§416.45 HR' },
  ],
  CR: [
    { label: 'Records security — files secured, screens locked', standard_ref: '§416.47 CR' },
  ],
  EM: [
    { label: 'Emergency equipment — crash cart sealed, AED checked', standard_ref: '§416.52 EM' },
    { label: 'Emergency plan — posted, staff aware of roles', standard_ref: '§416.54 EM' },
  ],
  ADA: [
    { label: 'Accessible pathways — clear, adequate width', standard_ref: 'ADA' },
    { label: 'Signage — braille/tactile where required', standard_ref: 'ADA' },
  ],
  WASTE: [
    { label: 'Biohazard containers — labeled, not overfull, lid closed', standard_ref: 'OSHA/EPA WASTE' },
    { label: 'Waste segregation — regulated vs general waste separated', standard_ref: 'OSHA/EPA WASTE' },
  ],
  WT: [
    { label: 'CLIA waiver — current, posted', standard_ref: '§416.49 WT' },
  ],
}

export const DAILY_OPERATIONAL_CONTROL_FEATURE_PREFIX = 'daily_controls.' as const

export const DAILY_OPERATIONAL_CONTROLS = [
  {
    key: 'or_temperature',
    label: 'OR temperature log is current and within the facility range',
    domain: 'ENV',
    standard_ref: 'ASHRAE-ENV-003',
    requires_severity: true,
    verification: true,
    yes_evidence_prompt: 'Capture an image of the OR temperature log showing today\u2019s readings in range.',
  },
  {
    key: 'glucometer_checks',
    label: 'Glucometer quality checks are complete for the day',
    domain: 'WT',
    standard_ref: '§416.49 WT',
    requires_severity: true,
    verification: true,
    yes_evidence_prompt: 'Capture an image of the glucometer quality-check test results for today.',
  },
  {
    key: 'lab_checks',
    label: 'Daily lab and waived-testing checks are complete',
    domain: 'WT',
    standard_ref: '§416.49 WT',
    requires_severity: true,
    verification: true,
    yes_evidence_prompt: 'Capture an image of the lab / waived-testing checks log for today.',
  },
  {
    key: 'humidity',
    label: 'Humidity readings are current and within the facility range',
    domain: 'ENV',
    standard_ref: 'ASHRAE-ENV-003',
    requires_severity: true,
    verification: true,
    yes_evidence_prompt: 'Capture an image of the humidity readings log for today.',
  },
  {
    key: 'air_balance',
    label: 'Air balance or pressure relationship checks are complete',
    domain: 'ENV',
    standard_ref: 'ASHRAE-ENV-001',
    requires_severity: true,
    verification: true,
    yes_evidence_prompt: 'Capture an image of the air-balance / pressure-relationship reading for today.',
  },
  {
    key: 'medication_refrigerator_temperatures',
    label: 'Medication refrigerator temperatures are logged and within range',
    domain: 'MED',
    standard_ref: 'ENV-TMP-001',
    requires_severity: true,
    verification: true,
    yes_evidence_prompt: 'Capture an image of the medication refrigerator temperature log for today.',
  },
  {
    key: 'blanket_warmer_checks',
    label: 'Blanket warmer temperature checks are complete',
    domain: 'ENV',
    standard_ref: 'ENV-TMP-002',
    requires_severity: true,
    verification: true,
    yes_evidence_prompt: 'Capture an image of the blanket-warmer temperature log for today.',
  },
  {
    key: 'fluid_warmer_checks',
    label: 'Fluid warmer temperature checks are complete',
    domain: 'ENV',
    standard_ref: 'ENV-TMP-002',
    requires_severity: true,
    verification: true,
    yes_evidence_prompt: 'Capture an image of the fluid-warmer temperature log for today.',
  },
] as const satisfies ReadonlyArray<{
  key: string
  label: string
  domain: SurveyDomain
  standard_ref: string
  requires_severity: boolean
  verification: boolean
  yes_evidence_prompt: string
}>

export type DailyOperationalControlKey = (typeof DAILY_OPERATIONAL_CONTROLS)[number]['key']

export interface DailyAssessmentFeatureFlagLike {
  feature_key: string
  enabled: boolean
}

export function buildDailyAssessmentConfigFromFeatureFlags(
  flags: DailyAssessmentFeatureFlagLike[] = [],
): DailyAssessmentFacilityConfig {
  const knownKeys = new Set(DAILY_OPERATIONAL_CONTROLS.map((control) => control.key))
  const operationalControls: Partial<Record<DailyOperationalControlKey, boolean>> = {}

  for (const flag of flags) {
    if (!flag.feature_key.startsWith(DAILY_OPERATIONAL_CONTROL_FEATURE_PREFIX)) continue
    const key = flag.feature_key.slice(DAILY_OPERATIONAL_CONTROL_FEATURE_PREFIX.length)
    if (!knownKeys.has(key as DailyOperationalControlKey)) continue
    operationalControls[key as DailyOperationalControlKey] = flag.enabled
  }

  return { operationalControls }
}

function isOperationalControlEnabled(
  key: DailyOperationalControlKey,
  config?: DailyAssessmentFacilityConfig,
): boolean {
  return config?.operationalControls?.[key] !== false
}

// ── Engine ──────────────────────────────────────────────────────────────────

/**
 * Get today's daily assessment plan.
 *
 * Edge case handling:
 * - Mid-year start: pass `startDate` to offset operating day numbering so
 *   day 0 aligns to the facility's first operating day, not Jan 1.
 * - Area changes: pass a custom `rotation` to override DEFAULT_ROTATION.
 *   Cycle length adapts automatically.
 */
export function getDailyPlan(
  date: Date,
  customHolidays: string[] = [],
  options?: {
    startDate?: Date
    rotation?: RotationSlot[]
    facilityConfig?: DailyAssessmentFacilityConfig
  },
): DailyAssessmentPlan | null {
  const operatingDay = options?.startDate
    ? getOperatingDayNumberFrom(date, options.startDate, customHolidays)
    : getOperatingDayNumber(date, customHolidays)
  if (operatingDay < 0) return null // non-operating day

  const rotation = options?.rotation ?? DEFAULT_ROTATION
  const cycleLength = rotation.length
  const rotationDay = operatingDay % cycleLength
  const slot = rotation[rotationDay]

  const items: DailyAssessmentItem[] = []
  for (const control of DAILY_OPERATIONAL_CONTROLS) {
    if (!isOperationalControlEnabled(control.key, options?.facilityConfig)) continue
    items.push({
      item_id: `daily-${control.key}`,
      label: control.label,
      domain: control.domain,
      standard_ref: control.standard_ref,
      evidence_type: ['photo'],
      control_type: 'operational',
      config_key: control.key,
      requires_severity: control.requires_severity,
      verification: control.verification,
      yes_evidence_prompt: control.yes_evidence_prompt,
    })
  }

  for (const domain of slot.domains) {
    const domainItems = DOMAIN_ITEMS[domain] ?? []
    for (const di of domainItems) {
      items.push({
        item_id: `${slot.areas[0]}-${domain}-${items.length}`,
        label: di.label,
        domain: domain as SurveyDomain,
        standard_ref: di.standard_ref,
        evidence_type: ['photo'],
        control_type: 'rotational',
      })
    }
  }

  // Estimate: ~1.5 min per item (photo + rate + optional note)
  const estimatedMinutes = Math.round(items.length * 1.5)

  return {
    rotationDay,
    area: slot.areas[0] as FacilityArea,
    combinedAreas: slot.areas as unknown as FacilityArea[],
    domains: slot.domains as unknown as SurveyDomain[],
    items,
    estimatedMinutes,
  }
}

/**
 * Get the rotation schedule label for display.
 */
export function getRotationLabel(rotationDay: number): string {
  const slot = DEFAULT_ROTATION[rotationDay % DEFAULT_ROTATION.length]
  return slot.label
}

/**
 * Total number of days in one rotation cycle.
 */
export function getRotationCycleLength(): number {
  return DEFAULT_ROTATION.length
}

/**
 * Calculate area average from responses.
 * Returns 1.0 (all green) to 3.0 (all red). Lower is better.
 */
export function calculateAreaAverage(responses: DailyAssessmentResponse[]): number {
  const scoredResponses = responses.filter((response) => response.response !== 'na')
  if (scoredResponses.length === 0) return 0
  const sum = scoredResponses.reduce((acc, r) => acc + r.rating, 0)
  return Math.round((sum / scoredResponses.length) * 100) / 100
}

/**
 * Calculate domain averages from responses + items.
 */
export function calculateDomainAverages(
  items: DailyAssessmentItem[],
  responses: DailyAssessmentResponse[],
): Map<SurveyDomain, number> {
  const responseMap = new Map(responses.map(r => [r.item_id, r.rating]))
  const domainSums = new Map<SurveyDomain, { total: number; count: number }>()

  for (const item of items) {
    const rating = responseMap.get(item.item_id)
    if (rating === undefined) continue
    const existing = domainSums.get(item.domain) ?? { total: 0, count: 0 }
    existing.total += rating
    existing.count++
    domainSums.set(item.domain, existing)
  }

  const averages = new Map<SurveyDomain, number>()
  for (const [domain, { total, count }] of domainSums) {
    averages.set(domain, Math.round((total / count) * 100) / 100)
  }
  return averages
}

// ── Mid-year start helper ──────────────────────────────────────────────────

/**
 * Get operating day number relative to a custom start date (mid-year start).
 * Returns -1 if date is before startDate or is a non-operating day.
 */
function getOperatingDayNumberFrom(
  date: Date,
  startDate: Date,
  customHolidays: string[],
): number {
  if (date < startDate) return -1
  if (isNonOperatingDay(date, customHolidays)) return -1

  let operatingDay = 0
  for (let d = new Date(startDate); d < date; d.setDate(d.getDate() + 1)) {
    if (!isNonOperatingDay(d, customHolidays)) {
      operatingDay++
    }
  }
  return operatingDay
}

// ── Export ──────────────────────────────────────────────────────────────────

export interface DailyAssessmentExportRow {
  date: string
  area: string
  item_id: string
  label: string
  domain: string
  standard_ref: string
  rating: number
  rating_label: string
  note: string
  assessed_at: string
}

/**
 * Export daily assessment history as structured rows suitable for CSV.
 * For accreditation evidence — shows every rated item with context.
 */
export function exportAssessmentHistory(
  results: DailyAssessmentResult[],
): DailyAssessmentExportRow[] {
  const rows: DailyAssessmentExportRow[] = []

  for (const result of results) {
    // Reconstruct items for the area by generating a plan for that date
    for (const response of result.responses) {
      rows.push({
        date: result.date,
        area: result.area,
        item_id: response.item_id,
        label: response.item_id, // will be enriched when items are available
        domain: '',
        standard_ref: '',
        rating: response.rating,
        rating_label: RATING_LABELS[response.rating],
        note: response.note,
        assessed_at: response.assessed_at,
      })
    }
  }

  return rows
}

/**
 * Convert export rows to CSV string.
 */
export function exportRowsToCsv(rows: DailyAssessmentExportRow[]): string {
  const headers = [
    'Date', 'Area', 'Item ID', 'Label', 'Domain', 'Standard Ref',
    'Rating', 'Rating Label', 'Note', 'Assessed At',
  ]
  const csvRows = [headers.join(',')]

  for (const row of rows) {
    csvRows.push([
      row.date,
      escapeCsv(row.area),
      row.item_id,
      escapeCsv(row.label),
      row.domain,
      escapeCsv(row.standard_ref),
      String(row.rating),
      row.rating_label,
      escapeCsv(row.note),
      row.assessed_at,
    ].join(','))
  }

  return csvRows.join('\n')
}

function escapeCsv(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

/**
 * Trigger a CSV download in the browser.
 */
export function downloadCsv(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}
